import { PIN_HEIGHT, PIN_WIDTH, PinInput, escapeHtml, cssUrl, luminance, readableOn, requireImageUrl } from "./types";

export function venueFeatureTemplate(input: PinInput): string {
  const { eyebrow, brand, brandLogo, cta, palette, typography, venue } = input;
  const imageUrl = requireImageUrl(input, "venueFeature");
  if (!venue) {
    throw new Error("venueFeature template requires venue { name, location?, tagline? }");
  }

  // For dark palettes (e.g. moodyDark), `palette.ink` is light cream and
  // would disappear against the white eyebrow chip. Pick whichever of
  // {bg, ink} is darker for the chip text — guarantees contrast.
  const isDarkPalette = luminance(palette.bg) < luminance(palette.ink);
  const chipInk = isDarkPalette ? palette.bg : palette.ink;

  // Headline scales with length so every title fits TWO lines (a third
  // row bloats the card and eats the photo). Thresholds tuned for DM
  // Serif Display at 904px usable width (~4.3px/char per font px).
  const nameLen = venue.name.length;
  const nameSize = nameLen <= 22 ? 96 : nameLen <= 32 ? 82 : nameLen <= 42 ? 72 : 62;
  // Logo filter: black for light palettes, white for dark palettes
  const brandLogoFilter = isDarkPalette
    ? "brightness(0) invert(1)"
    : "brightness(0)";

  // Subject-aware layout: when the photo's subject is low in the frame the
  // generator sets cardPosition:"top" so the card covers the empty upper
  // area instead of burying the subject. The card and the eyebrow chip
  // swap ends; the gradient is symmetric (darkens top AND bottom equally)
  // so it works either way with no change.
  const cardTop = input.cardPosition === "top";
  const cardEdge = cardTop ? "top:48px" : "bottom:48px";
  const chipEdge = cardTop ? "bottom:48px" : "top:48px";

  // Contrast guards for client-brand overlays: named palettes are hand-tuned,
  // but B2B clients can supply light-on-light bg/ink/soft combos. Guard every
  // palette-on-palette slot in the card.
  const cardInk = readableOn(palette.bg, palette.ink);
  const locationInk = readableOn(palette.soft, palette.ink);
  const ctaInk = readableOn(palette.ink, palette.bg);

  return `<!doctype html>
<html><head>
<meta charset="utf-8" />
<link rel="preconnect" href="https://fonts.googleapis.com" />
<link rel="preconnect" href="https://fonts.gstatic.com" crossorigin />
<link rel="stylesheet" href="${typography.googleFontsHref}" />
<style>
  *{margin:0;padding:0;box-sizing:border-box}
  html,body{width:${PIN_WIDTH}px;height:${PIN_HEIGHT}px;font-family:${typography.bodyFont};-webkit-font-smoothing:antialiased;background:${palette.bg}}
  .pin{position:relative;width:100%;height:100%;overflow:hidden}
  .image{position:absolute;inset:0;background-image:url('${cssUrl(imageUrl)}');background-size:cover;background-position:center}
  .image::after{content:"";position:absolute;inset:0;background:linear-gradient(180deg,rgba(0,0,0,.35) 0%,rgba(0,0,0,0) 30%,rgba(0,0,0,0) 60%,rgba(0,0,0,.35) 100%)}
  /* Type scale note: pins render at 1000×1500 but live in the FEED at
     ~200-280px wide (≈0.22×). Anything under ~30px render is invisible
     there. Headline targets ~20px+ at feed scale; secondary text ~9-10px.
     Line clamps keep the larger type from overflowing the card when copy
     runs long. */
  .top{position:absolute;${chipEdge};left:48px;right:48px;display:flex;justify-content:space-between;align-items:center;z-index:2}
  .featured{background:rgba(255,255,255,.95);color:${chipInk};padding:18px 32px;border-radius:999px;font-weight:700;font-size:27px;letter-spacing:4px;text-transform:uppercase;display:inline-flex;align-items:center;gap:12px}
  .featured::before{content:"";width:11px;height:11px;border-radius:50%;background:${chipInk}}
  .card{position:absolute;left:48px;right:48px;${cardEdge};background:color-mix(in srgb, ${palette.bg} 85%, transparent);color:${cardInk};border-radius:28px;padding:44px 48px;box-shadow:0 30px 80px rgba(0,0,0,.35);z-index:2}
  ${eyebrow ? `.eyebrow{font-size:24px;letter-spacing:6px;text-transform:uppercase;color:${palette.primary};font-weight:700;margin-bottom:16px}` : ""}
  .name{font-family:${typography.headlineFont};font-size:${nameSize}px;font-weight:${typography.headlineWeight};line-height:1.1;letter-spacing:-2px;margin-bottom:20px;padding-bottom:.08em;display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden}
  .name em{font-style:italic;font-weight:400;color:${palette.accentText ?? palette.ink}}
  .location{display:inline-flex;align-items:center;gap:10px;background:${palette.soft};color:${locationInk};padding:13px 26px;border-radius:999px;font-weight:600;font-size:30px;letter-spacing:.5px;margin-bottom:22px}
  .location svg{width:30px;height:30px}
  .tagline{font-size:44px;line-height:1.3;color:${cardInk};opacity:.9;margin-bottom:28px;display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden}
  .row{display:flex;justify-content:space-between;align-items:center;gap:24px;padding-top:24px;border-top:1.5px solid ${palette.soft}}
  .brand{font-weight:800;letter-spacing:3px;text-transform:uppercase;font-size:28px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
  .brand-logo{height:56px;width:auto;display:block;filter:${brandLogoFilter}}
  .cta{background:${palette.ink};color:${ctaInk};padding:20px 38px;border-radius:999px;font-weight:600;font-size:28px;letter-spacing:.5px;white-space:nowrap;flex-shrink:0}
</style></head>
<body><div class="pin">
  <div class="image"></div>
  <div class="top">
    <span class="featured">${escapeHtml(eyebrow ?? "Sauntera Pick")}</span>
  </div>
  <div class="card">
    <div class="name">${escapeHtml(venue.name)}</div>
    ${
      venue.location
        ? `<span class="location"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="M12 21s-7-7.5-7-12a7 7 0 1 1 14 0c0 4.5-7 12-7 12z"/><circle cx="12" cy="9" r="2.5"/></svg>${escapeHtml(venue.location)}</span>`
        : ""
    }
    ${venue.tagline ? `<div class="tagline">${escapeHtml(venue.tagline)}</div>` : ""}
    <div class="row">
      ${
        input.hideLogo
          ? ""
          : brandLogo
            ? `<img class="brand-logo" src="${brandLogo}" alt="${escapeHtml(brand)}" />`
            : `<span class="brand">${escapeHtml(brand)}</span>`
      }
      <span class="cta">${escapeHtml(cta ?? "Discover This Hidden Gem")} →</span>
    </div>
  </div>
</div></body></html>`;
}
